import { defineWorkflow } from './workflow-builder'

/**
 * WF-CLM-01 (Customer Claim Handling) — nomor workflow 13, entitas `claim`. v7.50 (laporan 23 §2.3 C-01 … C-10, keputusan
 * user §9): alur dipersingkat menjadi
 *
 *   Admin "Terima Barang & Terbitkan Tugas" → Sortir Berjalan (petugas scan + putuskan tiap unit: Bagus, atau Buka →
 *   Good / Repack / Not Good) → Menunggu Approval → Supervisor Setujui (atau Minta Sortir Ulang, catatan wajib, hasil
 *   tetap, tanpa batas) / sistem menyetujui otomatis menurut Master SLA `CLM_AUTO_APPROVE` → Disetujui → Admin Proses
 *   Claim → Tugaskan Putaway → Diproses → sistem menutup Selesai saat seluruh tugas putaway claim selesai.
 *
 * - Claim lahir langsung di `CLM_SORTING` (status awal) saat tugas sortir terbit; tidak ada aksi "issue" terpisah
 *   (penerbitan tercatat dari `claim.user_created` / `date_created` + tx_log `clm-terima-tugas`).
 * - Dua aksi sistem tanpa eligibility (dijalankan handler lewat `systemTransition`, pola WF-GAB-01): `ACT_CLM_AUTO_APPROVE`
 *   dan `ACT_CLM_AUTO_COMPLETE`.
 * - Dihapus dari v7.44: rencana kedatangan, pendataan / pencocokan, Izin Bongkar (D1), Tolak Claim, tugas bongkar
 *   terpisah, jalur Kadiv, penyimpanan tiga kartu, order pengganti, Tanpa Retur (status `CLM_PENDING_ARRIVAL`,
 *   `CLM_COUNTING`, `CLM_MATCHING`, `CLM_PENDING_UNLOAD`, `CLM_UNLOAD_ALLOWED`, `CLM_UNLOADING`, `CLM_STORING`,
 *   `CLM_COMPLETED_REPLACED`, `CLM_REJECTED`, `CLM_NO_RETURN`). Kadiv WH FG hanya melihat.
 */
export const WF_CLM = defineWorkflow({
  no: 13,
  code: 'WF-CLM-01',
  name: 'Customer Claim Workflow',
  entity: 'claim',
  statuses: [
    ['CLM_SORTING', 'Sortir Berjalan', true, false, '#3B82F6', 'Petugas men-scan dan memutuskan tiap unit claim'],
    ['CLM_PENDING_SORT_APP', 'Menunggu Approval', false, false, '#F59E0B', 'Hasil sortir menunggu Supervisor WH FG'],
    ['CLM_SORT_APPROVED', 'Disetujui', false, false, '#10B981', 'Hasil sortir disetujui, Admin memproses claim'],
    ['CLM_PROCESSING', 'Diproses', false, false, '#06B6D4', 'Label pack tercetak, tugas putaway berjalan'],
    ['CLM_COMPLETED', 'Selesai', false, true, '#059669', 'Seluruh barang claim sudah tersimpan']
  ],
  actors: [
    ['ACT_CLM_ADMIN_FG', 'Admin WH FG', 'admin_wh_fg', null],
    ['ACT_CLM_OP_FG', 'Operator WH FG', 'operator_wh_fg', null],
    ['ACT_CLM_SPV_FG', 'Supervisor WH FG', 'supervisor_wh_fg', null],
    ['ACT_CLM_SALES', 'Sales / Marketing', 'sales_marketing', { id: { _null: false } }]
  ],
  components: [
    ['CMP_CLM_FINISH_SORT', 'Tombol Selesai Sortir', 'ACTION', {}],
    ['CMP_CLM_APPROVE', 'Tombol Setujui Hasil Sortir', 'ACTION', { color: '#10B981', variant: 'primary' }],
    ['CMP_CLM_RESORT', 'Tombol Minta Sortir Ulang', 'ACTION', { variant: 'outlined' }],
    ['CMP_CLM_AUTO_APPROVE', 'Aksi Auto-Approve Hasil Sortir', 'ACTION', {}],
    ['CMP_CLM_PROCESS', 'Tombol Proses Claim — Tugaskan Putaway', 'ACTION', { variant: 'primary' }],
    ['CMP_CLM_AUTO_COMPLETE', 'Aksi Selesai Otomatis', 'ACTION', {}],
    ['CMP_CLM_PANEL_HEADER', 'Panel Header Claim', 'PANEL', {}],
    ['CMP_CLM_PANEL_SORT', 'Panel Hasil Sortir', 'PANEL', {}],
    ['CMP_CLM_PANEL_PUTAWAY', 'Panel Putaway Claim', 'PANEL', {}]
  ],
  actions: [
    ['ACT_CLM_FINISH_SORT', 'CLM_SORTING', 'CLM_PENDING_SORT_APP', 'CMP_CLM_FINISH_SORT', false, false, null],
    ['ACT_CLM_SPV_APPROVE', 'CLM_PENDING_SORT_APP', 'CLM_SORT_APPROVED', 'CMP_CLM_APPROVE', false, false, null],
    ['ACT_CLM_RESORT', 'CLM_PENDING_SORT_APP', 'CLM_SORTING', 'CMP_CLM_RESORT', true, true, null],
    ['ACT_CLM_AUTO_APPROVE', 'CLM_PENDING_SORT_APP', 'CLM_SORT_APPROVED', 'CMP_CLM_AUTO_APPROVE', false, false, null],
    ['ACT_CLM_PROCESS', 'CLM_SORT_APPROVED', 'CLM_PROCESSING', 'CMP_CLM_PROCESS', false, false, null],
    [
      'ACT_CLM_AUTO_COMPLETE',
      'CLM_PROCESSING',
      'CLM_COMPLETED',
      'CMP_CLM_AUTO_COMPLETE',
      false,
      false,
      { is_completed: true, completed_at: '$NOW' }
    ]
  ],
  eligibility: [
    ['ACT_CLM_FINISH_SORT', 'ACT_CLM_OP_FG'],
    ['ACT_CLM_SPV_APPROVE', 'ACT_CLM_SPV_FG'],
    ['ACT_CLM_RESORT', 'ACT_CLM_SPV_FG'],
    ['ACT_CLM_PROCESS', 'ACT_CLM_ADMIN_FG']
  ],
  panels: [
    ['CMP_CLM_PANEL_HEADER', 'CLM_SORTING', 'read', null],
    ['CMP_CLM_PANEL_HEADER', 'CLM_PENDING_SORT_APP', 'read', null],
    ['CMP_CLM_PANEL_SORT', 'CLM_SORTING', 'write', 'ACT_CLM_OP_FG'],
    ['CMP_CLM_PANEL_SORT', 'CLM_PENDING_SORT_APP', 'read', null],
    ['CMP_CLM_PANEL_PUTAWAY', 'CLM_PROCESSING', 'read', null]
  ],
  emailStatuses: ['CLM_PENDING_SORT_APP']
})

export type ClmStatusCode = (typeof WF_CLM.spec.statuses)[number][0]
export type ClmActorCode = (typeof WF_CLM.spec.actors)[number][0]
export type ClmActionName = (typeof WF_CLM.spec.actions)[number][0]
export const buildWorkflowClmSeed = WF_CLM.build
